import { useTranslation } from 'react-i18next';

import type { Addendum } from '@/api/interviews';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import { onDateTime } from '@/pages/PersonCard/cardDates';
import { type } from '@/theme/typography';

interface Props {
  /** The payload's own `addenda`, in the order the server sends them — oldest first. */
  addenda: Addendum[];
  /** The viewer, so their own entry can say *"You"* — the §6.5 precedent, and no name anywhere. */
  viewerId: number;
}

/**
 * §6.7's addenda — **below the original, each with its own timestamp, and never merged into it** (ADR
 * 0008, protection #5).
 *
 * ⚠️ **The rendering is the protection.** §6.7 says it as a prohibition: *"Do not render the latest
 * version and hide the history — the point is that both are visible."* An interface that folded an
 * addendum into the original would produce exactly what ADR 0008 exists to prevent — feedback that reads
 * as though it had always said this — while every backend test stayed green, because nothing on the server
 * changed. So the original is above, untouched, and each addendum is a dated entry of its own.
 *
 * ⚠️ **Zero addenda renders nothing at all** — no empty block, no *"No addenda"*, and **no count.** There
 * is deliberately no `addendum_count` anywhere in this product: a count invites reading a number of
 * changes of mind as a signal about the person who wrote them, which is a judgement about the
 * interviewer, not about the candidate.
 *
 * ⚠️ **An addendum is itself immutable**, so nothing here is a control: no edit, no delete, no menu. A
 * typo in an addendum is answered by **another addendum** — ADR 0008's *"typos are corrected through the
 * same mechanism"* — and several on one interview are ordinary rather than a defect. The API offers
 * exactly one operation under `…/addenda/` and it is the `POST`.
 *
 * **Every entry is signed** since ADR 0016 (2026-07-30): `AddendumOut` carries `author_name` beside the
 * `author_id` it always had. This paragraph said *"no author name, because no payload carries one … the
 * date alone for anybody else's"* until then — a real limitation for two sprints, not a preference. The
 * reader's **own** entry still says *"You"*, which is the product's word on §6.5 as well.
 */
export function AddendumList({ addenda, viewerId }: Props) {
  const { t, i18n } = useTranslation('interviews');

  if (addenda.length === 0) return null;

  return (
    <section className="mt-10">
      <Eyebrow count={addenda.length}>{t('addendum.eyebrow')}</Eyebrow>

      <ol className="mt-3 divide-y divide-rule">
        {addenda.map((addendum) => (
          <li key={addendum.id} className="py-4 first:pt-0">
            <p className={cn(type.metadata, type.numeral)}>
              <time dateTime={addendum.created_at}>
                {onDateTime(addendum.created_at, i18n.language)}
              </time>
              <span className={cn(type.metadata, 'ml-2 font-medium not-italic')}>
                {addendum.author_id === viewerId ? t('addendum.you') : addendum.author_name}
              </span>
            </p>
            {/* §3's *Feedback comment* measure: an addendum is feedback and is read, not scanned. */}
            <p className={cn(type.feedbackComment, 'mt-2 whitespace-pre-line')}>{addendum.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
